/* ==========================================================================
   FILE: modules/auth/components/LoginForm.jsx
   IAMONEAI - Login Form Component
   Handles IIN-based authentication
   ========================================================================== */

import React, { useState, useCallback } from 'react';
import { Button, Input } from '../../../global/components';
import { useAPI } from '../../../global/hooks/useAPI';
import { formatIIN, validateIIN } from './validation';
import './LoginForm.css';

// ==========================================================================
// LOGIN FORM COMPONENT
// ==========================================================================

/**
 * Login form with IIN authentication
 * 
 * @param {Object} props
 * @param {Function} onSuccess - Called with user data on successful login
 * @param {Function} onRegisterClick - Navigate to register page
 * @param {Function} onForgotClick - Navigate to forgot IIN page
 */
function LoginForm({
  onSuccess,
  onRegisterClick,
  onForgotClick,
  className = '',
}) {
  // Form state
  const [iin, setIIN] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // API hook
  const api = useAPI();

  // Handle IIN input with formatting
  const handleIINChange = useCallback((e) => {
    const formatted = formatIIN(e.target.value);
    setIIN(formatted);
    setError(''); // Clear error on input
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');

    // Validate IIN format
    const validation = validateIIN(iin);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setIsLoading(true);

    try {
      // Call brain API to verify IIN exists and get user data
      const brainResponse = await api.getBrain(iin);

      if (!brainResponse || !brainResponse.brain) {
        setError('IIN not found. Please check your IIN or register a new account.');
        setIsLoading(false);
        return;
      }

      // Build user data from brain response
      const userData = {
        iin,
        brain: brainResponse.brain,
        identity: {
          iin,
          owner: brainResponse.brain.metadata?.owner || 'User',
          email: brainResponse.brain.metadata?.email,
        },
      };

      // Store in localStorage if remember me is checked
      if (rememberMe) {
        localStorage.setItem('iamoneai_iin', iin);
        localStorage.setItem('iamoneai_user', JSON.stringify(userData));
      } else {
        sessionStorage.setItem('iamoneai_iin', iin);
        sessionStorage.setItem('iamoneai_user', JSON.stringify(userData));
      }

      // Call success callback
      onSuccess?.(userData);

    } catch (err) {
      console.error('Login error:', err);
      
      if (err.message?.includes('404') || err.message?.includes('not found')) {
        setError('IIN not found. Please check your IIN or register a new account.');
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        setError('Unable to connect. Please check your internet connection.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [iin, rememberMe, api, onSuccess]);

  // Handle paste - auto-format IIN
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const formatted = formatIIN(pastedText);
    setIIN(formatted);
  }, []);

  const formClasses = [
    'login-form',
    className,
  ].filter(Boolean).join(' ');

  return (
    <form className={formClasses} onSubmit={handleSubmit}>
      {/* Header */}
      <div className="login-form-header">
        <h1 className="login-form-title">Welcome Back</h1>
        <p className="login-form-subtitle">
          Enter your IIN to access your AI guardian
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="login-form-error">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* IIN Input */}
      <div className="login-form-field">
        <Input
          label="Your IIN"
          placeholder="XXXX-XXXX-XXXX-XXXX"
          value={iin}
          onChange={handleIINChange}
          onPaste={handlePaste}
          error={error && !iin ? 'IIN is required' : ''}
          disabled={isLoading}
          autoComplete="off"
          spellCheck={false}
          maxLength={19}
          className="login-form-iin-input"
        />
        <p className="login-form-hint">
          Your IAMONEAI Identity Number (e.g., 20AA-2512-8A46-0988)
        </p>
      </div>

      {/* Remember Me */}
      <label className="login-form-remember">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          disabled={isLoading}
        />
        <span className="login-form-remember-checkmark" />
        <span className="login-form-remember-label">Remember me on this device</span>
      </label>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={!iin || iin.length < 19}
      >
        {isLoading ? 'Signing in...' : 'Sign In'}
      </Button>

      {/* Forgot IIN Link */}
      {onForgotClick && (
        <button
          type="button"
          className="login-form-forgot"
          onClick={onForgotClick}
          disabled={isLoading}
        >
          Forgot your IIN?
        </button>
      )}

      {/* Divider */}
      <div className="login-form-divider">
        <span>Don't have an account?</span>
      </div>

      {/* Register Link */}
      <Button
        type="button"
        variant="outline"
        size="lg"
        fullWidth
        onClick={onRegisterClick}
        disabled={isLoading}
      >
        Create Your Identity
      </Button>

      {/* Security Note */}
      <p className="login-form-security">
        <LockIcon />
        <span>Your IIN is your key. Never share it with anyone.</span>
      </p>
    </form>
  );
}

// ==========================================================================
// ICONS
// ==========================================================================

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default LoginForm;